import { MoneyFormat } from "../../MoneyFormat";
import {
  FILTER_INITIAL_OBJECT,
} from "../../../screens/Rankings/RankingsUtils";

export const INITIAL_STATE = {
  optimisationPercent: 20,
  yieldPercent: 75,
  yieldEnabled: false,
  currentRepeat: 0,
  optimisedRepeat: 0,
  currentLabourCost: 0,
  optimisedLabourCost: 0,
  projectedSavings: 0,
  expectedSavings: 0,
  savingsPercentage: 0,
  estimatedAnnualSavings: 0,
  thresholdType: "",
  justification: "",
  engineeringObservations: "",
  maintenanceConsiderations: "",
  supportingNotes: "",
  search: "",
  optimisationStatus: "Pending",
  assignTo: undefined,
  dueDate: null,
  priority: "Medium",
};

export const getRowId = (row) => String(row?.ranked_id ?? row?.rankedId ?? row?.id ?? row?.key ?? "");

export const normalizeMatchValue = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

export const getChipLabel = (row, searchQuery = "") => {
  const q = normalizeMatchValue(searchQuery);
  if (q && normalizeMatchValue(row?.card_no).includes(q)) {
    return row.card_no;
  }
  return row?.spec_concat || row?.spec || row?.card_no || "Specification";
};

export const CHIP_PAGE_SIZE = 10;
export const SUMMARY_PAGE_SIZE_DEFAULT = 10;

/* A summary figure of 0 is a real value, not a missing one, so `Number(x) || fallback`
   would silently replace it with the fallback. Only fall back when the API left the
   field out entirely or sent something that is not a number. */
export const numberOrFallback = (value, fallback) => {
  if (value === "" || value === null || value === undefined) return fallback;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

export const formatMoney = (value) => {
  if (value === "" || value === null || value === undefined) return "$0";
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric === 0) return "$0";
  const absolute = MoneyFormat(Math.abs(numeric));
  return numeric < 0 ? `-$${absolute}` : `$${absolute}`;
};

export const formatPercent = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "0%";
  return `${Number.isInteger(numeric) ? numeric : numeric.toFixed(2)}%`;
};

export const formatNumber = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "0.00";
  return numeric.toFixed(2);
};

export const formatInterval = (value, intervalType) => {
  if (value === "" || value === null || value === undefined) return "—";
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return String(value);
  const formatted = Number.isInteger(numeric)
    ? String(numeric)
    : numeric.toFixed(2);
  return intervalType ? `${formatted} ${intervalType}` : formatted;
};

export const formatCompliance = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return value ?? "—";
  return numeric.toLocaleString();
};

export const getRowYieldPercent = (row) => {
  const raw = Number(row?.yield_percentage);
  if (!Number.isFinite(raw)) return 75;
  return raw <= 1 ? Math.round(raw * 100) : Math.round(raw);
};

export const getImprovementStatus = (currentInterval, optimisedInterval, projectedSavings) => {
  const current = Number(currentInterval);
  const optimised = Number(optimisedInterval);
  if (Number.isFinite(current) && Number.isFinite(optimised)) {
    if (optimised > current) return "Improved";
    if (optimised < current) return "Degraded";
  }
  if (Number(projectedSavings) > 0) return "Improved";
  if (Number(projectedSavings) < 0) return "Degraded";
  return "Improved";
};

export const mergeRowsById = (existingRows = [], incomingRows = []) => {
  const map = new Map();
  for (let i = 0; i < existingRows.length; i++) {
    const row = existingRows[i];
    const id = getRowId(row);
    if (id) map.set(id, row);
  }
  for (let i = 0; i < incomingRows.length; i++) {
    const row = incomingRows[i];
    const id = getRowId(row);
    if (id) map.set(id, { ...map.get(id), ...row });
  }
  return Array.from(map.values());
};

export const isSpecSummaryItem = (row) =>
  Boolean(
    row &&
      (row.spec_concat ||
        row.spec ||
        row.card_no ||
        row.ranked_id ||
        row.current_interval != null ||
        row.projected_savings != null),
  );

export const mapApiItemToSummaryRow = (apiRow, key) => {
  const intervalType = apiRow?.interval_type || "";
  const currentInterval =
    apiRow?.current_interval ?? apiRow?.currentInterval ?? 0;
  const optimisedInterval =
    apiRow?.optimised_interval ??
    apiRow?.optimisedInterval ??
    currentInterval;
  const appliedOptimization =
    apiRow?.applied_optimisation ??
    apiRow?.applied_optimization ??
    apiRow?.appliedOptimization;
  const appliedYield = apiRow?.applied_yield ?? apiRow?.appliedYield;
  const projectedSavings = Number(apiRow?.projected_savings) || 0;
  const expectedSavings = Number(apiRow?.expected_savings) || 0;

  return {
    key: String(
      apiRow?.ranked_id ?? apiRow?.id ?? apiRow?.key ?? key,
    ),
    rankedId: apiRow?.ranked_id ?? apiRow?.id ?? null,
    reviewId: apiRow?.review_id ?? null,
    itemId: apiRow?.id ?? null,
    spec: apiRow?.spec_concat || apiRow?.spec || "—",
    cardNo: apiRow?.card_no || "—",
    taskDescription: apiRow?.task_description || "",
    compliance: formatCompliance(apiRow?.compliance_count),
    currentInterval: formatInterval(currentInterval, intervalType),
    optimisedInterval: formatInterval(optimisedInterval, intervalType),
    appliedOptimization:
      appliedOptimization === null || appliedOptimization === undefined
        ? "—"
        : `${appliedOptimization}%`,
    appliedYield:
      appliedYield === null || appliedYield === undefined
        ? "—"
        : `${appliedYield}%`,
    projectedSavings,
    estimatedSavings: expectedSavings,
    status: apiRow?.status || "Pending",
    row: apiRow,
  };
};

export const flattenReviewListToSummaryRows = (reviews, preferredReviewId = null) => {
  if (!Array.isArray(reviews) || reviews.length === 0) return [];

  const preferred = preferredReviewId != null
    ? reviews.filter(
        (review) =>
          Number(review?.id ?? review?.review_id) === Number(preferredReviewId),
      )
    : [];
  const source = preferred.length > 0 ? preferred : reviews;
  const rows = [];

  source.forEach((review, reviewIndex) => {
    const nestedItems = Array.isArray(review?.items)
      ? review.items
      : Array.isArray(review?.data)
        ? review.data
        : null;

    if (nestedItems?.length) {
      nestedItems.forEach((item, itemIndex) => {
        rows.push(
          mapApiItemToSummaryRow(
            {
              ...item,
              review_id: review?.id ?? review?.review_id ?? item?.review_id,
            },
            `${review?.id ?? reviewIndex}-${itemIndex}`,
          ),
        );
      });
      return;
    }

    if (isSpecSummaryItem(review)) {
      rows.push(mapApiItemToSummaryRow(review, reviewIndex));
    }
  });

  return rows;
};

export const PRIORITY_OPTIONS = [
  { value: "Low", label: "Low" },
  { value: "Medium", label: "Medium" },
  { value: "High", label: "High" },
];

export const OPTIMISATION_STATUS_OPTIONS = [
  { value: "In Progress", label: "In Progress" },
  { value: "Pending", label: "Pending" },
  { value: "Fallout Tasks", label: "Fallout Tasks" },
  { value: "NEW", label: "NEW" },
];
